import { Button, Icon, Navbar, Tag } from 'react-bulma-components';
import { INSTRUMENTS_BY_KEY, INSTRUMENT_ICON_BY_NAME, INSTRUMENT_BY_NAME } from '../lib/instruments';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { faSignInAlt, faSignOutAlt, faStopCircle, faUserGraduate, faUserShield } from '@fortawesome/free-solid-svg-icons'
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IsAdmin from '../containers/isadmin';
import { colors } from '../styles';
import { navigate } from "@reach/router";
import { useAuth } from 'react-oidc-context';
import { useGlobalState } from '../state';
import { useMusicEngine } from '../audio/useMusicEngine';

const NAVBAR_PROFILE = gql`
  query NavbarProfile {
    myProfile {
      id
      name
      email
      instrument {
        name
        key
        clef
      }
    }
  }
`;

const UPDATE_MY_INSTRUMENT = gql`
  mutation UpdateMyInstrument($input: UserProfileInput!) {
    updateMyProfile(input: $input) {
      id
      instrument {
        name
        key
        clef
      }
    }
  }
`;

const LoginNavbarItem = ({ handleNavbarItemClick}) => {
  const auth = useAuth();
  return (
    <Navbar.Item onClick={() => handleNavbarItemClick(null, () => auth.signinRedirect())}>
      Log in&nbsp;<Icon><FontAwesomeIcon icon={faSignInAlt} color={colors.primary} /></Icon>
    </Navbar.Item>
  )
};

const LoggedInNavbarItem = ({ handleNavbarItemClick}) => {
  const auth = useAuth();
  const client = useApolloClient();
  const { data } = useQuery(NAVBAR_PROFILE);

  const displayName = data?.myProfile?.name
    || data?.myProfile?.email
    || auth.user?.profile?.preferred_username
    || '';

  function logOut() {
    client.resetStore();
    auth.signoutRedirect({
      post_logout_redirect_uri: process.env.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI,
    });
  }
  return (
    <Navbar.Item hoverable >
      <Navbar.Link>
        <Icon><FontAwesomeIcon icon={faUserGraduate} color={colors.primary} /></Icon>&nbsp;{displayName}
      </Navbar.Link>
      <Navbar.Dropdown>
        <Navbar.Item onClick={() => handleNavbarItemClick("/profile")}>
          <Icon><FontAwesomeIcon icon={faUserGraduate} color={colors.primary} /></Icon>&nbsp;Profile
        </Navbar.Item>
        <Navbar.Item onClick={() => handleNavbarItemClick(null, logOut)}>
          <Icon><FontAwesomeIcon icon={faSignOutAlt} color={colors.primary} /></Icon>&nbsp;Log out
        </Navbar.Item>
      </Navbar.Dropdown>
    </Navbar.Item>
  )
}
const UserInstrumentNavbarItem = () => {
  const auth = useAuth();
  const { data } = useQuery(NAVBAR_PROFILE);
  const [updateMyProfile] = useMutation(UPDATE_MY_INSTRUMENT);
  const [, setCurrentInstrument] = useGlobalState('instrument');
  const [, setCurrentInstrumentClef] = useGlobalState('instrumentClef');

  if (!auth.isAuthenticated) return null;

  const instrumentName = data?.myProfile?.instrument?.name;
  const icon = instrumentName && INSTRUMENT_ICON_BY_NAME[instrumentName];

  const chooseInstrument = (name) => {
    const instrument = INSTRUMENT_BY_NAME[name];
    updateMyProfile({ variables: { input: { instrument } } });
    setCurrentInstrument(instrument.key);
    setCurrentInstrumentClef(instrument.clef);
  };

  return (
    // Hidden on mobile/tablet (where the burger menu renders dropdowns fully
    // expanded, not as a popup) — the full instrument list would make the
    // mobile menu very long. Change instrument via /profile there instead.
    <Navbar.Item hoverable className="is-hidden-touch">
      <Navbar.Link>
        <p>{icon && <span>{icon}&nbsp;</span>}{instrumentName || 'Choose instrument'}</p>
      </Navbar.Link>
      <Navbar.Dropdown>
        {Object.entries(INSTRUMENTS_BY_KEY).map(([key, instruments], groupIndex) => (
          <Fragment key={key}>
            {groupIndex > 0 && <hr className="navbar-divider" />}
            <div className="navbar-item has-text-weight-bold has-text-grey">{key} instruments</div>
            {instruments.map(({ name }) => (
              <Navbar.Item key={name} onClick={() => chooseInstrument(name)}>
                {INSTRUMENT_ICON_BY_NAME[name]}&nbsp;{name}
              </Navbar.Item>
            ))}
          </Fragment>
        ))}
      </Navbar.Dropdown>
    </Navbar.Item>
  );
};

const ProfileNavbarItem = ( { handleNavbarItemClick}) => {
  const auth = useAuth();
  if (auth.isAuthenticated) {
    return (
      <Fragment>
        <LoggedInNavbarItem handleNavbarItemClick={handleNavbarItemClick}/>
      </Fragment>
    )
  }
  else {
    return (
      <Fragment>
        <LoginNavbarItem handleNavbarItemClick={handleNavbarItemClick}/>
      </Fragment>
    )
  }
}

function Navigation(props) {
  const [active, setActive] = useState(false);
  const [, setCurrentInstrument] = useGlobalState('instrument');
  const [, setCurrentInstrumentClef] = useGlobalState('instrumentClef');
  const { activePlayerId, pause } = useMusicEngine();
  const auth = useAuth();
  const { data: navProfileData } = useQuery(NAVBAR_PROFILE, { skip: !auth.isAuthenticated });
  const syncedInstrumentRef = useRef(false);

  // Default the transposition to the user's own saved instrument on login.
  // Users can change it any time via UserInstrumentNavbarItem, which updates
  // both their profile and this same global state — that per-session change
  // never fights this sync beyond the one time it runs per login.
  useEffect(() => {
    if (!auth.isAuthenticated) {
      syncedInstrumentRef.current = false;
      return;
    }
    if (!syncedInstrumentRef.current && navProfileData?.myProfile?.instrument?.key) {
      setCurrentInstrument(navProfileData.myProfile.instrument.key);
      setCurrentInstrumentClef(navProfileData.myProfile.instrument.clef);
      syncedInstrumentRef.current = true;
    }
  }, [auth.isAuthenticated, navProfileData, setCurrentInstrument, setCurrentInstrumentClef]);

  const handleNavbarItemClick = (path, action) => {
    setActive(false);
    if(path){
      navigate(path);
    }
    if(action) 
    { 
      action(); 
    }
  };

  return (
    <Fragment>
      <Navbar active={active} color="dark" className="navbar is-fixed-top">
        <Navbar.Brand>
          <Navbar.Item renderAs="a" onClick={() => handleNavbarItemClick("/")}>
            <Tag color="primary"><strong>TuneIn</strong></Tag>
          </Navbar.Item>
          <Navbar.Burger
            active={active.toString()}
            onClick={() => setActive(!active)}
          />
        </Navbar.Brand>
        <Navbar.Menu>
          <Navbar.Container>
            <Navbar.Item onClick={() => handleNavbarItemClick("/songs")}>Songs</Navbar.Item>
            <Navbar.Item onClick={() => handleNavbarItemClick("/facts")}>Facts</Navbar.Item>
            <Navbar.Item onClick={() => handleNavbarItemClick("/mylibrary")}>My libray</Navbar.Item>
            {/* <Navbar.Item onClick={() => handleNavbarItemClick("/profile")}>Profile</Navbar.Item> */}
            <Navbar.Item onClick={() => handleNavbarItemClick("/contact")}>Contact Us</Navbar.Item>
          </Navbar.Container>
          <Navbar.Container align="right">
            {activePlayerId && (
              <Navbar.Item>
                <Button
                  color="danger"
                  size="small"
                  title="Stoppa uppspelning"
                  onClick={() => pause(activePlayerId)}
                >
                  <Icon><FontAwesomeIcon icon={faStopCircle} /></Icon>
                  <span>Stop</span>
                </Button>
              </Navbar.Item>
            )}
          <IsAdmin>
              <Navbar.Item hoverable>
                <Navbar.Link>
                  <p><FontAwesomeIcon icon={faUserShield} color={colors.secondary} />&nbsp;Admin</p>
                </Navbar.Link>
                <Navbar.Dropdown>
                  <Navbar.Item onClick={() => handleNavbarItemClick("/chords")}>Chords</Navbar.Item>
                  <Navbar.Item onClick={() => handleNavbarItemClick("/scales")}>Scales</Navbar.Item>
                  <Navbar.Item onClick={() => handleNavbarItemClick("/song-add")}>Add song</Navbar.Item>
                  <Navbar.Item onClick={() => handleNavbarItemClick("/fact-add")}>Add fact</Navbar.Item>
                  <Navbar.Item onClick={() => handleNavbarItemClick("/library")}>Library</Navbar.Item>
                  <Navbar.Item onClick={() => handleNavbarItemClick("/snippets")}>Snippets</Navbar.Item>
                </Navbar.Dropdown>
              </Navbar.Item>
            </IsAdmin>
{/*             <AdminNavigation handleNavbarItemClick={handleNavbarItemClick}/> */}
            <UserInstrumentNavbarItem/>
            <ProfileNavbarItem handleNavbarItemClick={handleNavbarItemClick}/>
          </Navbar.Container>
          {/* <UserNavigation handleNavbarItemClick={handleNavbarItemClick} /> */}

        </Navbar.Menu>
      </Navbar>
    </Fragment>
  )
}
export default Navigation;